/**
 * useRealtimeTranscription
 *
 * Taps the candidate's microphone track (already being published by
 * LiveKit) and streams 16 kHz Int16 PCM to the backend's realtime
 * transcription WebSocket. The backend opens and closes one Azure
 * SpeechRecognizer session per question — the only signal we send is
 * the existing question-transition event, here repurposed via
 * `signalQuestionStart(n)` so it doubles as the per-question session
 * boundary.
 *
 * This hook is the safety net for the assessment transcript. The
 * primary path remains the LiveKit recording + post-hoc Whisper; the
 * realtime utterances only get consulted when Whisper produced nothing
 * for a given question. Failure here must therefore be invisible to
 * the candidate — every browser API touchpoint is guarded.
 */
import { useCallback, useEffect, useRef } from 'react';

import { RealtimeTranscriptionClient } from '../services/realtimeTranscriptionClient';

// 8 kHz halves the PCM bitrate to ~128 kbps so we don't fight LiveKit's
// adaptive video stream for upstream bandwidth on weak connections.
// Azure SpeechRecognizer is configured to expect 8 kHz on the backend.
const TARGET_SAMPLE_RATE_HZ = 8000;
const PROCESSOR_BUFFER_SIZE = 4096;

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL as string | undefined;

export interface UseRealtimeTranscriptionReturn {
  signalQuestionStart: (questionIndex: number) => void;
  signalEndOfQuestions: () => void;
}

export function useRealtimeTranscription(
  audioTrack: MediaStreamTrack | null | undefined,
  interviewId: string | null | undefined,
  enabled: boolean,
): UseRealtimeTranscriptionReturn {
  const clientRef = useRef<RealtimeTranscriptionClient | null>(null);

  useEffect(() => {
    if (!enabled || !audioTrack || !interviewId) {
      console.log(
        '[useRealtimeTranscription] not starting — enabled=%s hasTrack=%s hasInterviewId=%s',
        enabled,
        !!audioTrack,
        !!interviewId,
      );
      return undefined;
    }

    const wsUrl = buildWebSocketUrl(interviewId);
    if (!wsUrl) {
      console.warn('[useRealtimeTranscription] VITE_API_SERVER_URL missing — cannot build WS URL');
      return undefined;
    }

    console.log('[useRealtimeTranscription] opening WS to', wsUrl);
    const client = new RealtimeTranscriptionClient(wsUrl);
    clientRef.current = client;
    client.open();

    const teardownAudio = wireMicTap(audioTrack, (pcm16) => client.pushPcm(pcm16));

    return () => {
      teardownAudio();
      client.close();
      clientRef.current = null;
    };
  }, [enabled, audioTrack, interviewId]);

  const signalQuestionStart = useCallback((questionIndex: number) => {
    clientRef.current?.signalQuestionStart(questionIndex);
  }, []);

  const signalEndOfQuestions = useCallback(() => {
    clientRef.current?.signalEndOfQuestions();
  }, []);

  return { signalQuestionStart, signalEndOfQuestions };
}

// ---------------------------------------------------------------------------
// Internal: URL + audio pipeline
// ---------------------------------------------------------------------------

function buildWebSocketUrl(interviewId: string): string | null {
  if (!apiServerUrl) return null;
  const wsScheme = apiServerUrl.startsWith('https') ? 'wss' : 'ws';
  const stripped = apiServerUrl.replace(/^https?/, wsScheme);
  return `${stripped}/api/interviews/${interviewId}/realtime-transcription`;
}

/**
 * Wires the candidate's mic into a 16 kHz Int16 PCM sink. Returns a
 * teardown function that disconnects and closes the AudioContext.
 *
 * Uses ScriptProcessor — deprecated but supported everywhere and
 * already the pattern used elsewhere in this codebase for live audio
 * forwarding. AudioWorklet would be the modern choice but adds a
 * separate-file constraint that pays off only at higher sample rates.
 */
function wireMicTap(
  audioTrack: MediaStreamTrack,
  onPcm: (pcm16: Int16Array) => void,
): () => void {
  let audioCtx: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let processor: ScriptProcessorNode | null = null;

  try {
    const stream = new MediaStream([audioTrack]);
    audioCtx = new AudioContext();
    source = audioCtx.createMediaStreamSource(stream);
    processor = audioCtx.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);

    const sourceRate = audioCtx.sampleRate;
    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      const pcm = downsampleFloat32ToInt16(input, sourceRate, TARGET_SAMPLE_RATE_HZ);
      if (pcm.length > 0) onPcm(pcm);
    };
    source.connect(processor);
    // Connecting to destination is required for the processor to fire
    // in some browsers; muted output prevents echo to the candidate.
    processor.connect(audioCtx.destination);
  } catch {
    // If anything in the audio graph blows up, surface no error — the
    // fallback is best-effort.
    return () => {};
  }

  return () => {
    try {
      processor?.disconnect();
    } catch {
      /* noop */
    }
    try {
      source?.disconnect();
    } catch {
      /* noop */
    }
    try {
      audioCtx?.close();
    } catch {
      /* noop */
    }
  };
}

function downsampleFloat32ToInt16(
  input: Float32Array,
  sourceRate: number,
  targetRate: number,
): Int16Array {
  if (targetRate >= sourceRate) return floatToInt16(input);
  const ratio = sourceRate / targetRate;
  const outLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    // Average the source samples that fall into this target sample's
    // window — cheap anti-aliasing, much better than naive subsample.
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j += 1) {
      sum += input[j];
      count += 1;
    }
    const sample = count > 0 ? sum / count : 0;
    output[i] = clampToInt16(sample);
  }
  return output;
}

function floatToInt16(input: Float32Array): Int16Array {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    output[i] = clampToInt16(input[i]);
  }
  return output;
}

function clampToInt16(value: number): number {
  const scaled = value < 0 ? value * 0x8000 : value * 0x7fff;
  if (scaled > 0x7fff) return 0x7fff;
  if (scaled < -0x8000) return -0x8000;
  return scaled | 0;
}
